<template>
    <div class="player-holder" ref="player">
        <NavigateEventsBar />
        <PlayerError :show="error" :message="errorMessage"/>
        <AutoplayTimer :show="showAutoplayTimer"/>
        <LoadingPlayer :show="showLoading"/>
    </div>
</template>

<script>
/* global config */
import { mapGetters, mapState } from 'vuex';
import AutoplayTimer from './AutoplayTimer';
import NavigateEventsBar from './NavigateEventsBar';
import LoadingPlayer from './LoadingPlayer';
import PlayerError from '../../components/PlayerError';
import constants from '../../helpers/constants';
import defaultThumbnail from '../../../assets/img/thumbnail.jpg';
import analyticHelper from '../../helpers/analyticHelper';

export default {
    name: 'Player',
    components: {
        AutoplayTimer,
        NavigateEventsBar,
        LoadingPlayer,
        PlayerError
    },
    data() {
        return {
            player: null,
            source: null,
            showAutoplayTimer: false,
            showLoading: false,
            error: false,
            errorMessage: constants.errorMessages.webSDKPlaying
        }
    },
    mounted() {
        const { Player, SDKApi } = window.PixellotWebSDK;
        SDKApi.projectId(config.projectID);
        const playerOptions = {
            autoplay: true,
            pano: {
                panoEnabled: true,
                recorder: {
                    outputFilename: 'Recorded clip (' + new Date().toUTCString() + ')'
                },
                showZoomIndicator: true
            }
        };
        this.player = new Player(this.$refs.player, playerOptions);
        this.player.volume = 0.5;
        this.player.pause();

        /** add event listeners on the current playback **/
        this.addEventListeners();

        if (this.activePlayback) {
            this.preparePlayerSource();
        }
    },
    beforeDestroy() {
        this.removeEventListeners();
    },
    watch: {
        activePlaybackId(nextProp, prevProp) {
            /** disable previous playback error **/
            this.disableError();

            /** hide timer when new active playback **/
            this.hideTimer();

            if (nextProp) {
                this.showLoading = false;
                this.preparePlayerSource();
            }

            if (prevProp && !nextProp) {
                this.showLoading = true;
            }
        },
        activePlaybackThumbnail(nextProp, prevProp){
            if (!prevProp) {
                this.setPoster(nextProp);
            }
        }
    },
    methods: {
        preparePlayerSource() {
            this.source = {};
            const urls = this.activePlayback.attributes.urls;

            switch (this.activePlayback.type) {
                case 'clip':
                    this.source.url = urls.clip;
                    break;
                case 'hd':
                case 'pano':
                    this.source.url = {
                        hd: urls.hd || null,
                        pano: urls.pano || null,
                    };
                    break;
                case 'player-highlight':
                case 'autohighlight':
                    this.source.url = urls.highlight;
                    break;
                case 'highlight':
                    this.source.url = this.activePlayback.attributes.urls.highlight;
                    break;
                default:
                    throw new Error(`Playback type: ${this.activePlayback.type} isn't supported`)
            }

            this.source.name = this.activePlayback.attributes.name;
            this.source.thumbnail = urls.thumbnail || defaultThumbnail;

            /** compose and set analytic option object **/
            const analyticOptions = analyticHelper.getAnalyticOptions(this.activePlayback);

            this.player.analytics.setOptions(analyticOptions);

            this.player.setSrc(this.source.url);
            this.player.currentTime = this.activePlayback.startTime || 0;
            this.setPoster(this.source.thumbnail);
        },
        setPoster(thumbnail) {

            /** In case of missing thumbnail ('', "", null, undefined, wrong url),
             * default background will be set (black) **/
            this.player.poster = thumbnail;
        },
        showTimer() {
            if (this.nextPlayback) {
                this.showAutoplayTimer = true;
            }
        },
        hideTimer() {
            this.showAutoplayTimer = false;
        },
        enableError() {
            this.error = true;
            document.querySelector('.video-js.vjs-default-skin').classList.add('vjs-error');
        },
        disableError() {
            if (this.error) {
                this.error = false;
                document.querySelector('.video-js.vjs-default-skin').classList.remove('vjs-error');
            }
        },
        onSourceModeChanged ({ mode }) {
            const analyticsOptions = {
                entityType: mode === 'hd' ? 'Event HD' : 'Event Pano',
            };

            if (this.activePlayback.type === 'hd') {
                this.player.analytics.updateOptions(analyticsOptions);
            }
        },
        addEventListeners() {
            this.player.on('ended', this.showTimer);
            this.player.on('seeking', this.hideTimer);
            this.player.on('error', this.enableError);
            this.player.on('source-changed', this.onSourceModeChanged);

            document.addEventListener('keydown', this.handleKeyPress);
        },
        removeEventListeners () {
            this.player.off('ended', this.showTimer);
            this.player.off('seeking', this.hideTimer);
            this.player.off('error', this.enableError);
            this.player.off('source-changed', this.onSourceModeChanged);

            document.removeEventListener('keydown', this.handleKeyPress);
        }
    },
    computed: {
        ...mapGetters({
            activePlayback: 'main/activePlayback',
            nextPlayback: 'main/nextPlayback',
            activePlaybackThumbnail: 'main/activePlaybackThumbnail'
        }),
        ...mapState({
            activePlaybackId: state => state.main.video.activePlaybackId
        })
    }
};
</script>
